import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { StarIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Badge } from '@nesso/ui/primitives/badge';
import { Button } from '@nesso/ui/primitives/button';
import { CurrencyInputField } from '@nesso/ui/primitives/currency-input-field';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@nesso/ui/primitives/table';

import {
    formatRateForInput,
    parseRateInput,
    type Service,
    useUpdateServiceRatesOptions,
} from '@/entities/service-catalog';

import {
    type EditServiceRatesFormValues,
    editServiceRatesFormSchema,
} from '../model/edit-service-rates-form.schema';

type Props = {
    services: Service[];
    onSaved: () => void;
};

// A feature cannot import `services-table-row.tsx`'s copy, so this duplicates it locally.
function DefaultBadge() {
    return (
        <Badge variant="info" className="gap-1">
            <StarIcon className="size-3 fill-current" />
            Default
        </Badge>
    );
}

function toFormValues(services: Service[]): EditServiceRatesFormValues {
    return {
        rates: services.map((service) => ({
            code: service.code,
            rate: formatRateForInput(service.rateCents),
        })),
    };
}

// Edit All Rates (Figma node 217:3802): only the Rate column becomes editable, with Save and the
// one missing-rate message sharing the footer row.
export function EditServiceRatesTable({ services, onSaved }: Props) {
    // A refetch must not add or drop rows: fields are registered by index against these rows.
    const [rows] = useState(services);
    const updateRates = useMutation(useUpdateServiceRatesOptions());
    const { control, handleSubmit, formState } = useForm<EditServiceRatesFormValues>({
        resolver: zodResolver(editServiceRatesFormSchema),
        defaultValues: toFormValues(rows),
    });
    // Below `xs` the Rate and spacer columns drop and the one rate field moves into the
    // Description cell, next to the badge/duration stack (matches the read-only services table).
    const isPhone = useBreakpointDown('xs');

    const onSubmit = handleSubmit((data) => {
        updateRates.mutate(
            {
                rates: data.rates.map(({ code, rate }) => ({
                    code,
                    rateCents: parseRateInput(rate),
                })),
            },
            {
                onSuccess: () => {
                    toast.success('Rates updated');
                    onSaved();
                },
            },
        );
    });

    // `formState.errors.rates` is react-hook-form's array-shaped error map, not a plain array, so
    // it is read defensively rather than with `Array.prototype.find`.
    const rateErrors = formState.errors.rates;
    const missingRateMessage = Array.isArray(rateErrors)
        ? rateErrors.find((entry) => entry?.rate)?.rate?.message
        : undefined;

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-0">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead variant="plain" className="w-26">
                            Code
                        </TableHead>
                        <TableHead variant="plain">Description</TableHead>
                        {!isPhone && (
                            <TableHead variant="plain" className="w-25 text-right">
                                Rate
                            </TableHead>
                        )}
                        {!isPhone && <TableHead variant="plain" className="w-12" />}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((service, index) => {
                        // Registered once per row; placed in the Description cell on phone or the
                        // Rate cell otherwise, so the field never registers twice.
                        const rateField = (
                            <Controller
                                control={control}
                                name={`rates.${index}.rate`}
                                render={({ field, fieldState }) => (
                                    <CurrencyInputField
                                        {...field}
                                        id={`edit-service-rate-${service.code}`}
                                        label={`Rate for ${service.description}`}
                                        hideLabel
                                        error={fieldState.invalid}
                                        className="text-right"
                                        fieldClassName={isPhone ? 'w-21' : undefined}
                                    />
                                )}
                            />
                        );

                        return (
                            <TableRow key={service.code}>
                                <TableCell className="h-18 w-26 font-semibold">
                                    {service.code}
                                </TableCell>
                                <TableCell className="h-18 whitespace-normal xs:whitespace-nowrap">
                                    {isPhone ? (
                                        <div className="flex flex-col gap-2">
                                            <div>
                                                {service.isDefault && (
                                                    <div className="mb-1">
                                                        <DefaultBadge />
                                                    </div>
                                                )}
                                                <div className="font-semibold">
                                                    {service.description}
                                                </div>
                                                <div className="text-muted-foreground text-sm">
                                                    {service.durationMinutes}m
                                                </div>
                                            </div>
                                            {rateField}
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">
                                                    {service.description}
                                                </span>
                                                {service.isDefault && <DefaultBadge />}
                                            </div>
                                            <span className="text-muted-foreground text-sm">
                                                {service.durationMinutes}m
                                            </span>
                                        </div>
                                    )}
                                </TableCell>
                                {!isPhone && (
                                    <TableCell className="h-18 w-25">{rateField}</TableCell>
                                )}
                                {!isPhone && <TableCell className="h-18 w-12" />}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <div className="sticky bottom-0 flex items-center justify-between gap-4 border-t border-border bg-background px-2 py-4">
                <Button type="submit" size="lg" disabled={updateRates.isPending}>
                    Save
                </Button>
                {missingRateMessage && (
                    <p role="alert" className="text-right text-sm font-medium text-destructive">
                        {missingRateMessage}
                    </p>
                )}
            </div>
        </form>
    );
}
